import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wrench, Zap, CheckCircle, FileText, HelpCircle, Settings, Cpu, PenTool } from "lucide-react";
import { useLang } from "../context/I18nContext";
import SectionVideo from "../components/SectionVideo";
import { SECTION_VIDEOS } from "../config/sectionVideos";

/**
 * Service & Wartung + Inbetriebnahme
 */
export default function Service() {
  const { t } = useLang();

  const commissioning = [
    "Funktionsprüfung", "Parametrierung", "Sensorprüfung", "Aktorenprüfung",
    "Heizkreisprüfung", "Temperaturkontrolle", "Regelungsprüfung", "Automationen",
    "App-Konfiguration", "Visualisierung", "Dokumentation", "Einschulung",
  ];

  const service = [
    "Wartung", "Funktionskontrollen", "Einstellungen", "Regelungsoptimierung",
    "Fehlersuche", "Komponententausch", "Anlagenerweiterung", "Softwareanpassung",
    "Systembetreuung",
  ];

  return (
    <div className="page-shell" data-testid="service-page">
      <section className="site-container page-hero">
        <div className="eyebrow mb-6 text-[#5FA637]">05 · {t("nav.service")}</div>
        <h1 className="page-title">
          Service <span className="italic-accent">& Wartung.</span>
        </h1>
        <p className="copy-lead mt-8 text-base md:text-lg">
          Eine technische Anlage ist erst fertig, wenn sie richtig eingestellt, geprüft und verständlich übergeben
          wurde. Betrieb und Erweiterung werden langfristig begleitet.
        </p>
      </section>

      {/* Section video — Service */}
      <SectionVideo {...SECTION_VIDEOS.service}/>

      {/* Inbetriebnahme */}
      <section className="site-container section-block border-t border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 reveal">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-5 text-[#5FA637]">01 · Inbetriebnahme</div>
            <div className="flex items-center gap-3 mb-5">
              <PenTool size={24} strokeWidth={1.5} className="text-[#5FA637]"/>
              <h2 className="section-title">Systemprüfung & Übergabe</h2>
            </div>
            <p className="text-white/70 leading-relaxed">
              Prüfung, Parametrierung und Einschulung — mit vollständiger Dokumentation. Jede Komponente wird
              einzeln geprüft und das Gesamtsystem im Zusammenspiel.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {commissioning.map((it, i) => (
                <div key={i} className="bg-[#0A1F38]/65 px-4 py-3 text-sm text-white/80 flex items-center gap-3">
                  <CheckCircle size={14} strokeWidth={1.6} className="text-[#5FA637] shrink-0"/>
                  {it}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service */}
      <section className="site-container section-block border-t border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 reveal">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-5 text-[#5FA637]">02 · Wartung</div>
            <div className="flex items-center gap-3 mb-5">
              <Wrench size={24} strokeWidth={1.5} className="text-[#5FA637]"/>
              <h2 className="section-title">Laufende Betreuung</h2>
            </div>
            <p className="text-white/70 leading-relaxed">
              Wartung, Optimierung und Erweiterung — damit die Anlage effizient bleibt und mit dem Gebäude
              mitwächst.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {service.map((it, i) => (
                <div key={i} className="bg-[#0A1F38]/65 px-4 py-3 text-sm text-white/80 flex items-center gap-3">
                  <Settings size={14} strokeWidth={1.6} className="text-[#5FA637] shrink-0"/>
                  {it}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="site-container section-block">
        <div className="cta-panel">
          <div className="eyebrow mb-5 text-[#5FA637]">Nächster Schritt</div>
          <h2 className="section-title max-w-3xl">
            Bestehende Anlage <span className="italic-accent">optimieren.</span>
          </h2>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/kontakt" className="btn-primary">
              {t("cta.serviceReq")} <ArrowRight size={14}/>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
